/**
 * VolatilityService
 *
 * RESPONSIBILITIES:
 * - Calculate volatility from price history using EWMA methodology
 * - Adapt granularity and lookback based on round duration settings
 * - Provide consistent volatility data across the application
 *
 * SHOULD NOT:
 * - Handle option pricing or Black-Scholes calculations
 * - Manage account balances or positions
 * - Handle order processing or execution
 */

import type { RoundDuration } from '@hooks/useSettings';
import { priceService } from '@services/priceService';
import type { Asset } from '@shared/types';
import { calculateVolatility } from '@utils/option-math';

export class VolatilityService {
  private static instance: VolatilityService | null = null;
  private volatilityData: Map<Asset, number> = new Map();
  private lastUpdate = 0;

  public static getInstance(): VolatilityService {
    VolatilityService.instance ??= new VolatilityService();
    return VolatilityService.instance;
  }

  private constructor() {}

  /**
   * Fetch volatility prices for an asset using proper historical candle data
   */
  public async fetchVolatilityPrices(
    asset: Asset,
  ): Promise<{ prices: number[]; priceGranularity: number }> {
    const now = Date.now();
    const priceGranularity = 300; // 5 minutes
    const candles = await priceService.fetchCandles(
      asset,
      '5m', // 5 minute candles
      undefined, // no limit
      new Date(now - 1000 * 60 * 60 * 24).toISOString(),
      new Date(now).toISOString(),
    );
    const prices = candles.map(c => parseFloat(c.close));
    return {
      prices,
      priceGranularity,
    };
  }

  /**
   * Calculate volatility from price data - requires data to be provided
   */
  public calculateVolatility(
    prices: number[],
    granularitySeconds: number,
  ): number {
    if (prices.length < 2) {
      return 0;
    }
    return calculateVolatility(prices, granularitySeconds);
  }

  /**
   * Store calculated volatility value for an asset
   */
  public setVolatility(asset: Asset, value: number): void {
    this.volatilityData.set(asset, value);
    this.lastUpdate = Date.now();
  }

  /**
   * Get cached volatility value for an asset
   * Returns null if no data available
   */
  public getVolatility(asset: Asset): number | null {
    return this.volatilityData.get(asset) ?? null;
  }

  /**
   * Get all cached volatility values
   * Returns partial data if some assets are missing
   */
  public getAllVolatilities(): Partial<Record<Asset, number>> {
    const result: Partial<Record<Asset, number>> = {};
    for (const [asset, volatility] of this.volatilityData.entries()) {
      result[asset] = volatility;
    }
    return result;
  }

  /**
   * Check if volatility data is stale (older than 2 minutes)
   */
  public isStale(): boolean {
    return Date.now() - this.lastUpdate > 2 * 60 * 1000;
  }

  /**
   * Fetch volatility data for all assets in parallel
   */
  public async fetchAllVolatilities(): Promise<Partial<Record<Asset, number>>> {
    const assets: Asset[] = ['BTC', 'ETH', 'SOL' /* , 'XRP' */];

    const results = await Promise.allSettled(
      assets.map(async asset => {
        const priceData = await this.fetchVolatilityPrices(asset);
        const volatility = this.calculateVolatility(
          priceData.prices,
          priceData.priceGranularity,
        );
        this.setVolatility(asset, volatility);
        return { asset, volatility };
      }),
    );

    const successfulResults: Partial<Record<Asset, number>> = {};
    results.forEach((result, index) => {
      if (result.status === 'fulfilled') {
        const { asset, volatility } = result.value;
        successfulResults[asset] = volatility;
      } else {
        console.warn(
          `Failed to fetch volatility for ${assets[index]}:`,
          result.reason,
        );
      }
    });

    console.log('VolatilityService: fetchAllVolatilities', successfulResults);
    return successfulResults;
  }

  /**
   * Get round duration from localStorage (same logic as RoundService)
   */
  private getRoundDuration(): RoundDuration {
    try {
      const stored = localStorage.getItem('yv_round_duration_v1');
      return stored ? JSON.parse(stored) : '1d';
    } catch {
      return '1d';
    }
  }
}

// Export singleton instance
export const volatilityService = VolatilityService.getInstance();
